import Visibility from "@mui/icons-material/Visibility";
import { Switch } from "@mui/material";
import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";

import { customNameComparator } from "../../../../helpers/coappDataGridUtils";
import { getUserColumnFilterValue } from "../../../../helpers/datagridFiltering";
import CoAppDataGridDateTimeCell from "../../../global/components/datagrid/CoAppDataGridDateTimeCell";
import CoAppDataGridListCell from "../../../global/components/datagrid/CoAppDataGridListCell";
import CoAppDataGridNameCell from "../../../global/components/datagrid/CoAppDataGridNameCell";
import RowActionIcons from "../../../global/components/RowActionIcons";

// `actions` is last so the row's three-dot menu sits on the right edge.
export const userColumns = ["user", "status", "roles", "groups", "lastHeartbeat", "createdAt", "workstationId", "actions"];

export interface UserRow {
	id: string;
	roles: string[];
	groups: string[];
	isCoAppSupport?: boolean;
	workstationId?: string | null;
	active: boolean;
	[key: string]: unknown;
}

type RenderParams = GridRenderCellParams<UserRow>;

type UserFilterRow = {
	firstname?: string;
	lastname?: string;
	email?: string;
	name?: string;
	[key: string]: unknown;
};
const userNameSortComparator: NonNullable<GridColDef<UserRow>["sortComparator"]> = (v1, v2, cellParams1, cellParams2) =>
	customNameComparator(v1, v2) ?? 0;

export const getUserManagementColumns = (
	viewUserPageFromUserManagement: (params: GridRenderCellParams) => void,
	handleOpenUserDeletionDialog: (rowObject: { params: GridRenderCellParams<UserRow> }) => void,
	handleUserStatusToggle: (id: string, state: boolean | "false" | "true") => void,
	organizationSsoStatus: boolean,
	handleWorkstationClick: (workstationId?: string | null) => void
): GridColDef<UserRow>[] => [
	{
		field: "user",
		headerName: "Name",
		sortable: true,
		sortComparator: userNameSortComparator,
		valueGetter: (value, row) => getUserColumnFilterValue(value, row as UserFilterRow),
		editable: false,
		width: 200,
		renderCell: (params: RenderParams) => (
			<CoAppDataGridNameCell item={params.row} isUser={true} isCoAppSuperAdmin={params.row.isCoAppSupport} />
		),
	},
	{
		field: "active",
		headerName: "Status",
		sortable: true,
		editable: false,
		width: 90,
		renderCell: (params: RenderParams) => (
			<Switch
						checked={params.row.active}
						disabled={organizationSsoStatus || params.row.isCoAppSupport}
						onChange={() => handleUserStatusToggle(params.row.id, params.row.active === true ? "false" : "true")}
					/>
		),
	},
	{
		field: "roles",
		headerName: "Roles",
		sortable: false,
		editable: true,
		flex: 1,
		renderCell: (params: RenderParams) => (
			<CoAppDataGridListCell items={params.value} resource="Roles" isCoAppSuperAdmin={params.row.isCoAppSupport} />
		),
	},
	{
		field: "groups",
		headerName: "Groups",
		editable: false,
		sortable: false,
		flex: 1,
		renderCell: (params: RenderParams) => (
			<CoAppDataGridListCell items={params.value} resource="Groups" isCoAppSuperAdmin={params.row.isCoAppSupport} />
		),
	},
	{
		field: "lastHeartbeat",
		headerName: "Last heartbeat",
		editable: false,
		sortable: true,
		width: 200,
		valueGetter: (value) => (value == null || value === "" ? null : new Date(String(value))),
		renderCell: (params: RenderParams) => (
			<CoAppDataGridDateTimeCell value={params.value} isCoAppSuperAdmin={params.row.isCoAppSupport} />
		),
	},
	{
		field: "createdAt",
		headerName: "Created",
		sortable: true,
		editable: false,
		width: 200,
		valueGetter: (value) => (value == null || value === "" ? null : new Date(String(value))),
		renderCell: (params: RenderParams) => (
			<CoAppDataGridDateTimeCell value={params.value} isCoAppSuperAdmin={params.row.isCoAppSupport} />
		),
	},
	{
		field: "workstationId",
		headerName: "Workstation ID",
		editable: false,
		sortable: false,
		flex: 1,
		renderCell: (params: RenderParams) => (
			<div
						style={{ cursor: params.value ? "pointer" : "default" }}
						onClick={() => handleWorkstationClick(params.value)}
					>
						<CoAppDataGridNameCell item={{ name: params.value }} />
					</div>
		)
	},
	{
		field: "actions",
		type: "actions",
		width: organizationSsoStatus ? 60 : 100,
		sortable: false,
		filterable: false,
		hideable: false,
		headerName: "",
		align: "right",
		headerAlign: "right",
		renderCell: (params) => (
			organizationSsoStatus ? (
						<RowActionIcons
							actions={[{
								key: "view",
								label: "View user",
								icon: <Visibility />,
								onClick: () => viewUserPageFromUserManagement(params)
							}]}
						/>
					) : (
						<RowActionIcons
							onEdit={() => viewUserPageFromUserManagement(params)}
							onDelete={() => handleOpenUserDeletionDialog({ params })}
							deleteDisabled={params.row.isCoAppSupport}
							deleteDisabledLabel="Cannot delete Support Users"
						/>
					)
		)
	}
];
